import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { BrowseRow } from '../BrowseRow'
import { BrowsePane } from '../BrowsePane'
import { Failed, Loading } from '../../screens/Stub'
import {
  useEnsembles, useEntryState, useFeed, useFollows, usePeople, useSeries, useShowings, useSummaries, useVenues,
} from '../../lib/queries'
import { useDismissEntry, useForgetEntry, useSaveEntry } from '../../lib/mutations'
import { laneFor } from '../../lib/derive'
import { alreadyHappened, idsOf, mergeFilmRuns, offFeed, signalFeatures } from '../../lib/entries'
import {
  DESK_WHEN, KIND_DOT, KIND_LABEL, KINDS, WINDOWS, kindOf, matches, windowHit, type Kind, type WindowHit,
} from '../../lib/browse'
import { berlinDay, dayHeading, groupByDay, isRunning, selectionIn, stepSelection } from '../../lib/deskBrowse'
import { isSoldOut, useSoldOut } from '../../lib/tickets'
import { toast } from '../../ui/Toast'
import type { ShowingLite } from '../../components/CardBody'
import { holidayOn } from '../../lib/calendar'
import { useRegionDistricts, useRegionHolidays } from '../../lib/currentRegion'
import { useBerlinNow } from '../../lib/useBerlinNow'
import { ownPicture, rowDomId } from '../BrowseRow'
import type { FeedRow } from '../../lib/types'
import { go, useRoute } from '../../lib/router'
import { DESK_DEFAULTS, filtersFromRoute, narrows, routeFromFilters, type DeskFilters, type Narrow, type Win } from '../../lib/deskRoute'
import { activeFilters, whereOf, whereOptions } from '../../lib/deskFilters'
import { seriesName } from '../../lib/night'
import { isTypingTarget } from '../../lib/typing'
import { firstOn, isOnDay } from '../../lib/dayPane'
import { zoneOfCity } from '../../lib/zone'
import { CityStrip } from '../CityLines'
import '../browse.css'

type KindKey = Exclude<Kind, 'all'>

type Filters = DeskFilters
const NARROW_LABEL: Record<Narrow['by'], string> = { place: 'Place', person: 'Person', ensemble: 'Ensemble', series: 'Series' }

const DAYS_SHOWN = 10
const EXHIBITIONS_SHOWN = 10
const NONE: never[] = []
const ENGLISH = ['native', 'surtitles', 'subtitles']

/** One feed row with the questions the filters ask of it, answered once. */
interface Item {
  row: FeedRow
  kind: KindKey
  /** Where the row sits in each window, or null where it is not in it
   *  (a3-18: in every window any of its showings fall in, under its first
   *  day there). */
  win: Record<Win, WindowHit | null>
  /** Every future night sold out (a1-27): out of the list unless searched. */
  soldOut: boolean
  /** A running exhibition: counted apart from the days, as the list sets it. */
  running: boolean
  /** The Bezirk (`whereOf`), not the venue's own word for where it is. */
  district: string
  free: boolean
  english: boolean
}

const fold = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()

/** `browse.ts`'s search, and the names it does not read: a lineup, an artist
 *  list, a director. */
function searchable(row: FeedRow, q: string): boolean {
  if (matches(row, q)) return true
  const extra = [
    ...(row.night?.lineup ?? []).map(l => l.name),
    ...(row.exhibition?.artists ?? []),
    ...(row.film?.directors ?? []),
  ].map(fold).join(' ')
  const words = fold(q).trim().split(/\s+/)
  return words.every(w => extra.includes(w) || fold(`${row.title} ${row.venue ?? ''}`).includes(w))
}

/** `searching`: a1-27's sold-out rows come back for a search, as on the
 *  phone — hidden from the list, still findable. */
const passes = (
  i: Item, f: Filters, ok: Set<string> | null, searching: boolean, skip?: 'win' | 'kind' | 'district',
): boolean =>
  (searching || !i.soldOut) &&
  (skip === 'win' || !!i.win[f.win]) &&
  (skip === 'kind' || !f.kind || i.kind === f.kind) &&
  (skip === 'district' || !f.district || i.district === f.district) &&
  (!f.free || i.free) && (!f.english || i.english) && (!ok || ok.has(i.row.id))

const typing = isTypingTarget

/** Browse as a list for a monitor: a filter column, rows grouped by day each
 *  shaped for its kind, and a pane for the open one. */
export function Browse() {
  const feedQ = useFeed()
  const showQ = useShowings()
  const stateQ = useEntryState()
  const followsQ = useFollows()
  const saveM = useSaveEntry()
  const forgetM = useForgetEntry()
  const dismissM = useDismissEntry()
  const soldOutMarks = useSoldOut()

  const now = useBerlinNow()
  const today = useMemo(() => berlinDay(now.toISOString()), [now])
  // The current region's districts: Boston's Where list is its towns, not Berlin's Bezirke.
  const districts = useRegionDistricts()
  const holidayKey = useRegionHolidays()
  // The filters live in the route and nowhere else (a4-09): a phone link —
  // `?when=tonight`, `?person=`, `#/places/<id>` — opens the list it names,
  // and every change is written back, so the URL is the list. Only the search
  // box keeps a draft of its own, written after a pause as the phone's is.
  const route = useRoute()
  const f = useMemo(
    () => filtersFromRoute(route.tab === 'browse' ? route.id : null, route.tab === 'browse' ? route.query : {}, KINDS),
    [route],
  )
  const [qInput, setQInput] = useState(f.q)
  const q = useDeferredValue(qInput)
  // Back, forward, or a link while the screen is open: the box follows.
  useEffect(() => { setQInput(f.q) }, [f.q])
  // The filter column folded to a strip when the list is narrow (CSS decides
  // when); this is whether it is drawn out over the list.
  const [sideOut, setSideOut] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [moreDays, setMoreDays] = useState(false)
  const [allExhibitions, setAllExhibitions] = useState(false)
  // Rows hidden on this visit stay where they are, faded: the list does not
  // reflow under the pointer. Rows hidden on an earlier visit are gone.
  const [hiddenNow, setHiddenNow] = useState<ReadonlySet<string>>(new Set())

  const feed = feedQ.data ?? NONE
  const showRows = showQ.data?.rows ?? NONE
  const byEntry = showQ.data?.byEntry
  const stateRows = stateQ.data ?? NONE
  const follows = followsQ.data
  const venuesById = useVenues().data?.byId
  const narrow = f.narrow
  const peopleQ = usePeople(narrow?.by === 'person' ? [narrow.id] : NONE)
  const ensemblesQ = useEnsembles(narrow?.by === 'ensemble' ? [narrow.id] : NONE)
  const seriesQ = useSeries()
  // The name behind the id: the URL carries only the id, as the phone's does.
  const narrowName: string | null = !narrow ? null
    : narrow.by === 'place' ? venuesById?.get(narrow.id)?.name ?? (venuesById ? narrow.id : null)
      : narrow.by === 'person' ? peopleQ.data?.find(p => p.id === narrow.id)?.name ?? (peopleQ.isSuccess ? narrow.id : null)
        : narrow.by === 'ensemble' ? ensemblesQ.data?.find(e => e.id === narrow.id)?.name ?? (ensemblesQ.isSuccess ? narrow.id : null)
          : seriesName(seriesQ.data, narrow.id) ?? (seriesQ.isSuccess || seriesQ.isError ? narrow.id : null)

  const dismissed = useMemo(() => new Set(stateRows.filter(r => r.state === 'dismissed').map(r => r.entry_id)), [stateRows])
  const saved = useMemo(() => new Set(stateRows.filter(r => r.state === 'saved').map(r => r.entry_id)), [stateRows])

  const showingsOf = (row: FeedRow): ShowingLite[] =>
    idsOf(row).flatMap(id => byEntry?.get(id) ?? NONE)

  // The same base the phone's Browse runs on, less the language rules: In
  // English is a filter here, not a default.
  const items = useMemo<Item[]>(() => {
    // Since cards WP3 the view sends a film's set as one row, so this merges
    // only what the twins pass has not joined (`mergeFilmRuns`' own note).
    const rows = mergeFilmRuns(
      feed.filter(e =>
        (!dismissed.has(e.id) || hiddenNow.has(e.id)) && !alreadyHappened(e, today)
        && !laneFor(e).hidden && laneFor(e).id !== 'news' && !offFeed(e)),
      showRows, today)
    const out: Item[] = []
    for (const row of rows) {
      const own = showingsOf(row)
      const kind = kindOf(row)
      const running = isRunning(row, kind)
      // A row under a day is in a window by its days. `windowHit` also lets
      // a run overlap it, which put a theatre run starting in three days into
      // Tonight's count while its heading sat under a later day: the filter
      // said 134 and the day said 110. Only a running exhibition goes by
      // overlap, as it sits under no day.
      const dated = running ? row : { ...row, run_start: null, run_end: null }
      const hit = (w: Win) => windowHit(dated, w, now, own)
      // Every night it had has started (the grace passed) and none is left:
      // the list and every window's count drop it alike — the rule
      // `hasStarted` made for the next showing alone, which also dropped a
      // film whose 16:10 had started though its 18:30 had not.
      const any = hit('any')
      if (!any) continue
      out.push({
        row, kind, running,
        win: { tonight: hit('tonight'), weekend: hit('weekend'), week: hit('week'), month: hit('month'), any },
        soldOut: soldOutMarks.size > 0 && isSoldOut(row, own, soldOutMarks, now),
        district: whereOf(row.district, districts),
        free: !!row.is_free,
        english: ENGLISH.includes(row.english_access),
      })
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed, showRows, dismissed, hiddenNow, today, now, soldOutMarks, districts])

  // A person, an ensemble, a series or a place, and the Year's `day=`
  // (a3-20, `lib/dayPane.ts`, the rule the day pane counts by): the set they
  // narrow to, before any search does.
  const day = f.day
  const narrowed = useMemo(() => {
    if (!narrow && !day) return null
    const parentOf = (id: string) => venuesById?.get(id)?.parent_id
    return new Set(items.filter(i =>
      (!narrow || narrows(i.row, showingsOf(i.row), narrow, parentOf))
      && (!day || isOnDay(i.row, i.kind, showingsOf(i.row), day))).map(i => i.row.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, narrow, day, byEntry, venuesById])
  const searching = q.trim() !== ''
  const ok = useMemo(() => {
    if (!q.trim()) return narrowed
    return new Set(items.filter(i => (!narrowed || narrowed.has(i.row.id)) && searchable(i.row, q)).map(i => i.row.id))
  }, [items, q, narrowed])

  // The filters as the rows are matched: a district from the URL folded to
  // its Bezirk, as every row's is (`whereOf`), so an old `?district=Wedding`
  // link lists Mitte rather than nothing. A name it cannot place is kept and
  // matches nothing, which the empty state then names.
  const fm = useMemo(() => ({ ...f, district: whereOf(f.district, districts) || f.district }), [f, districts])

  // Dated events and running exhibitions are counted apart. a4-17 (Doug
  // chose: the running count on the Exhibitions row only): beside a window
  // the running count was the same 250-odd for every window, so it said
  // nothing about the window and read as an undecoded `· 252 on`. Where
  // counts both, as a Bezirk's exhibitions are its own.
  const counts = useMemo(() => {
    const zero = () => ({ n: 0, on: 0 })
    const win = Object.fromEntries(WINDOWS.map(({ when }) => [when, zero()])) as Record<Win, { n: number; on: number }>
    const kind: Partial<Record<KindKey, { n: number; on: number }>> = {}
    const districts = new Map<string, number>()
    const all = zero()
    const add = (c: { n: number; on: number }, i: Item) => { if (i.running) c.on++; else c.n++ }
    for (const i of items) {
      if (passes(i, fm, ok, searching, 'win')) for (const { when } of WINDOWS) if (i.win[when]) add(win[when], i)
      if (passes(i, fm, ok, searching, 'kind')) { add(kind[i.kind] ??= zero(), i); add(all, i) }
      if (passes(i, fm, ok, searching, 'district') && i.district) districts.set(i.district, (districts.get(i.district) ?? 0) + 1)
    }
    return { win, kind, all, districts }
  }, [items, fm, ok, searching])
  const num = (c: { n: number; on: number }, running = false) =>
    running && c.on ? (c.n ? `${c.n} · ${c.on} on` : `${c.on} on`) : String(c.n)

  const visible = useMemo(() => items.filter(i => passes(i, fm, ok, searching)), [items, fm, ok, searching])
  // A picture shared by three or more rows is the house's, not the event's:
  // the Philharmoniker's hall photograph on every concert it has not got a
  // picture for. A poster is exempt, being the film's own by construction.
  const shared = useMemo(() => {
    const n = new Map<string, number>()
    for (const i of items) {
      const key = i.row.hero_key ?? i.row.image_url
      if (key && i.kind !== 'film') n.set(key, (n.get(key) ?? 0) + 1)
    }
    return n
  }, [items])
  const picOf = (r: FeedRow, kind: string): string | null => {
    const key = r.hero_key ?? r.image_url
    if (kind !== 'film' && key && (shared.get(key) ?? 0) >= 3) return null
    return ownPicture(r, kind)
  }

  const kindById = useMemo(() => new Map(items.map(i => [i.row.id, i.kind])), [items])
  const rowKind = (r: FeedRow): string => kindById.get(r.id) ?? kindOf(r)

  // On one day, a row sits under that day at its time that day: a film
  // playing Tuesday and Thursday would otherwise head Tuesday on Thursday.
  // Otherwise (a3-18) each row stands on its first instant inside the window
  // — under that day, at that time — so a film on tonight and Saturday reads
  // as Saturday's in This weekend, and its "Also" line follows from there.
  const { days, exhibitions } = useMemo(
    () => groupByDay(visible.map(i => {
      const at = day ? firstOn(showingsOf(i.row), day, zoneOfCity(i.row.city)) : i.win[f.win]?.at
      return at && at !== (i.row.next_showing ?? i.row.next_opening) ? { ...i.row, next_showing: at } : i.row
    }), today, rowKind),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible, today, kindById, day, f.win, byEntry],
  )
  const shownDays = moreDays ? days : days.slice(0, DAYS_SHOWN)
  const shownExhibitions = allExhibitions ? exhibitions : exhibitions.slice(0, EXHIBITIONS_SHOWN)
  const order = useMemo(
    () => [...shownDays.flatMap(g => g.rows), ...shownExhibitions],
    [shownDays, shownExhibitions],
  )

  // The pane is for a row the list draws, or for nothing (a3-06,
  // `selectionIn`): a filter, a search or a fold of the days that takes the
  // open row out of the list closes its pane, and the selection goes with it
  // so the arrows start again from the top and widening the filter does not
  // reopen a pane nobody asked for.
  const orderIds = useMemo(() => order.map(r => r.id), [order])
  const openId = selectionIn(orderIds, selected)
  const open = openId ? order.find(r => r.id === openId) ?? null : null
  useEffect(() => {
    if (selected && !openId && !feedQ.isPending) setSelected(null)
  }, [selected, openId, feedQ.isPending])
  // One call per render for everything on screen, the pane's row included.
  const onScreen = useMemo(() => [...order, ...(open ? [open] : [])].flatMap(idsOf), [order, open])
  const summaries = useSummaries(onScreen).data
  const describe = (row: FeedRow) => {
    const ids = idsOf(row)
    const hit = ids.map(id => summaries?.[id]).find(Boolean)
    return { text: hit?.summary ?? row.film?.overview ?? null, url: hit?.source_url ?? null }
  }

  const isSaved = (r: FeedRow) => idsOf(r).some(id => saved.has(id))
  const isHidden = (r: FeedRow) => idsOf(r).some(id => dismissed.has(id))
  const doSave = (r: FeedRow) => {
    if (isSaved(r)) forgetM.mutate({ ids: idsOf(r) })
    else saveM.mutate({ ids: idsOf(r), features: signalFeatures(r) })
  }
  const doHide = (r: FeedRow) => {
    if (isHidden(r)) { forgetM.mutate({ ids: idsOf(r) }); return }
    setHiddenNow(prev => new Set(prev).add(r.id))
    dismissM.mutate({ ids: idsOf(r), features: signalFeatures(r) })
    // a1-20: the phone's words, "Hidden · Undo" — the row stays in the list
    // this visit (`hiddenNow`), pressed as Hidden, and Undo is the button's
    // own second press.
    toast({ text: 'Hidden', action: { label: 'Undo', onClick: () => forgetM.mutate({ ids: idsOf(r) }) } })
  }
  // Keys. Read through a ref so the listener is bound once and sees this render.
  const live = useRef({ orderIds, open, doSave, doHide, sideOut })
  live.current = { orderIds, open, doSave, doHide, sideOut }
  const searchRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const { orderIds, open, doSave, doHide, sideOut } = live.current
      if (e.key === 'Escape') {
        if (typing(e.target)) (e.target as HTMLElement).blur()
        else if (sideOut) setSideOut(false)
        else setSelected(null)
        return
      }
      if (typing(e.target)) return
      if (e.key === '/') {
        e.preventDefault()
        // Folded to a strip, the box is not drawn until the column is out.
        // Only then: drawn out is a state of the strip, not of the column.
        if (searchRef.current && !searchRef.current.offsetParent) {
          setSideOut(true)
          requestAnimationFrame(() => searchRef.current?.focus())
        } else searchRef.current?.focus()
        return
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        // From the open row, or else from the row Tab reached (a4-18), so
        // the first arrow after a Tab moves from where focus is, not the top.
        const focused = (document.activeElement as HTMLElement | null)?.closest<HTMLElement>('.br-row')
        const from = open?.id ?? focused?.dataset.id ?? null
        const next = stepSelection(orderIds, from, e.key === 'ArrowDown' ? 1 : -1)
        if (!next) return
        e.preventDefault()
        setSelected(next)
        const el = document.getElementById(rowDomId(next))
        el?.scrollIntoView({ block: 'nearest' })
        // Focus follows the selection while it is in the list, so Tab from
        // here reaches this row's Save and Not for me, not the first row's.
        if (focused || document.activeElement?.closest('.br-list')) el?.focus({ preventScroll: true })
        return
      }
      const row = open
      if (!row) return
      if (e.key === 's') doSave(row)
      if (e.key === 'h') doHide(row)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // The search box's draft reaches the URL after a pause: a `go` per
  // keystroke would put one history entry per letter on the back button.
  useEffect(() => {
    if (qInput === f.q) return
    const t = setTimeout(() => {
      const r = routeFromFilters({ ...f, q: qInput })
      go('browse', r.id, r.query)
    }, 400)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the draft retriggers it
  }, [qInput])

  // Every hook is above this line. The screen waits on the feed alone: the
  // showings ("Also …") and the saved and hidden marks fill in when they
  // arrive, and the filter column draws from the feed in the meantime.
  if (feedQ.error && !feedQ.data) return <Failed error={feedQ.error.message} />

  const set = (patch: Partial<Filters>) => {
    const r = routeFromFilters({ ...f, q: qInput, ...patch })
    go('browse', r.id, r.query)
    setMoreDays(false); setAllExhibitions(false)
  }
  // A kind a link asked for stays drawn, pressed, at 0: hiding it hid the
  // one filter that explains the empty list.
  const kinds = KINDS.filter(k => counts.kind[k] || f.kind === k)
  // a3-25: what is on, by name — the empty state's sentence, the standing
  // Clear, and the folded strip's count are this one list.
  // A `day=` link opens on Any time, as a person link does; that window is
  // the link's, not a filter Doug set, so it is not counted twice.
  const on = activeFilters({ ...f, q: qInput, win: day && f.win === 'any' ? DESK_WHEN : f.win }, {
    win: w => WINDOWS.find(x => x.when === w)?.label ?? w,
    kind: k => KIND_LABEL[k as KindKey] ?? k,
    narrow: narrowName,
  }, districts)
  // a3-20's `day=` narrows too; it counts as a filter Doug set and Clear drops it.
  if (day) on.push(dayHeading(day, today).long ?? dayHeading(day, today).name)
  const active = on.length
  const clear = () => { setQInput(''); set({ ...DESK_DEFAULTS }) }
  const where = whereOptions(counts.districts, f.district, districts)
  const whereValue = whereOf(f.district, districts) || f.district
  const running = visible.filter(i => i.running).length
  const total = visible.length - running

  // One row in the list takes Tab (a4-18, roving tabindex): the open one,
  // else the first.
  const tabRow = open?.id ?? order[0]?.id ?? null
  const rowProps = (row: FeedRow) => ({
    row, kind: rowKind(row), today,
    tabbable: row.id === tabRow,
    pic: picOf(row, rowKind(row)),
    description: describe(row).text,
    showings: showingsOf(row),
    selected: open?.id === row.id,
    hidden: isHidden(row),
    saved: isSaved(row),
    followed: !!row.venue_id && !!follows?.has(row.venue_id),
    onSelect: (id: string) => { setSelected(id); setSideOut(false) }, onSave: doSave, onHide: doHide,
  })

  return (
    <div className="br-wrap">
    <div className={`br${open ? ' paned' : ''}${sideOut ? ' side-out' : ''}`}>
      <div className="br-side">
        <button type="button" className="br-fold" aria-expanded={sideOut} aria-label="Filters" title="Filters"
          onClick={() => setSideOut(v => !v)}>
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 5h14M6 10h8M8.5 15h3" /></svg>
          {active > 0 && <span className="n">{active}</span>}
        </button>
        <div className="search">
          <input ref={searchRef} type="search" value={qInput} placeholder="Search  ( / )" aria-label="Search"
            onChange={e => setQInput(e.target.value)} />
        </div>
        {active > 0 && (
          <div className="br-clear">
            <span>{active} {active === 1 ? 'filter' : 'filters'} on</span>
            <button type="button" onClick={clear}>Clear</button>
          </div>
        )}
        {narrow && (
          <>
            <h3>{NARROW_LABEL[narrow.by]}</h3>
            <button type="button" className="opt narrow" aria-pressed="true" title="Clear"
              onClick={() => set({ narrow: null, ...(f.win === 'any' ? { win: 'any' } : {}) })}>
              <span className="nm">{narrowName ?? '…'}</span><span className="c">Clear</span>
            </button>
          </>
        )}
        {day && (
          <>
            <h3>Day</h3>
            <button type="button" className="opt narrow" aria-pressed="true" title="Clear"
              onClick={() => set({ day: null, ...(route.query.when ? {} : { win: narrow ? 'any' : DESK_WHEN }) })}>
              <span className="nm">{dayHeading(day, today).long ?? dayHeading(day, today).name}</span><span className="c">Clear</span>
            </button>
          </>
        )}
        <h3>When</h3>
        {/* A day (`day=`, a3-20) is the window: a window pressed replaces it,
            and its count would be the day's rows that also fall in it. */}
        {WINDOWS.map(({ when: w, label }) => (
          <button type="button" key={w} className="opt" aria-pressed={!day && f.win === w} onClick={() => set({ win: w, day: null })}>
            {label}<span className="c">{day ? '' : num(counts.win[w])}</span>
          </button>
        ))}
        <h3>What</h3>
        <button type="button" className="opt" aria-pressed={!f.kind} onClick={() => set({ kind: null })}>
          Everything<span className="c">{num(counts.all)}</span>
        </button>
        {kinds.map(k => (
          <button type="button" key={k} className="opt" aria-pressed={f.kind === k} onClick={() => set({ kind: k })}>
            <i style={{ background: KIND_DOT[k] }} />{KIND_LABEL[k]}<span className="c">{num(counts.kind[k] ?? { n: 0, on: 0 }, k === 'exhibition')}</span>
          </button>
        ))}
        <h3>Where</h3>
        <select value={whereValue} aria-label="District" onChange={e => set({ district: e.target.value })}>
          <option value="">Anywhere</option>
          {where.map(o => <option key={o.name} value={o.name}>{o.name} ({o.n})</option>)}
          {whereValue && !where.some(o => o.name === whereValue) && <option value={whereValue}>{whereValue} (0)</option>}
        </select>
        <h3>Only</h3>
        <button type="button" className="opt" aria-pressed={f.free} onClick={() => set({ free: !f.free })}>Free</button>
        <button type="button" className="opt" aria-pressed={f.english} onClick={() => set({ english: !f.english })}>In English</button>
      </div>

      <div className="br-col">
        <div className="br-count">
          <div>{feedQ.isPending ? 'Loading…' : `${total} ${total === 1 ? 'event' : 'events'}${running ? ` · ${running} exhibitions on` : ''}${narrowName ? ` · ${narrowName}` : ''}`}</div>
          {/* a4-18 (Doug chose: a one-line legend under the count). The keys
              existed and nothing on screen said so but `( / )`. */}
          <div className="keys" aria-hidden="true">↑↓ move · s save · h not for me · / search · Esc close</div>
          {(!day || day === today) && <CityStrip today={today} />}
        </div>
        <div className="br-list" role="listbox" aria-label="Events"
          aria-activedescendant={open ? rowDomId(open.id) : undefined}>
          {feedQ.isPending && <div className="br-wait"><Loading /></div>}
          {!feedQ.isPending && total + running === 0 && (
            <div className="empty">
              {on.length ? <>Nothing for {on.join(' · ')}.</> : 'Nothing on.'}
              {on.length > 0 && <button type="button" className="b" onClick={clear}>Clear filters</button>}
            </div>
          )}
          {shownDays.map((g, i) => {
            const h = dayHeading(g.day, today)
            const hol = holidayOn(g.day, 'Home', null, holidayKey)
            return (
              <section key={g.day}>
                <div className={`dayh${i === 0 ? ' first' : ''}`}>
                  <b>{h.name}</b>
                  <span>{[h.long, String(g.rows.length), hol].filter(Boolean).join(' · ')}</span>
                </div>
                {g.rows.map(r => <BrowseRow key={r.id} {...rowProps(r)} />)}
              </section>
            )
          })}
          {days.length > shownDays.length && (
            <button type="button" className="more" onClick={() => setMoreDays(true)}>
              Show {days.length - shownDays.length} more days
            </button>
          )}
          {exhibitions.length > 0 && (
            <section>
              <div className="dayh"><b>Exhibitions on now</b><span>{exhibitions.length} · by closing date</span></div>
              {shownExhibitions.map(r => <BrowseRow key={r.id} {...rowProps(r)} />)}
              {exhibitions.length > shownExhibitions.length && (
                <button type="button" className="more" onClick={() => setAllExhibitions(true)}>
                  Show all {exhibitions.length} exhibitions
                </button>
              )}
            </section>
          )}
        </div>
      </div>

      {open && (
        <BrowsePane
          key={open.id}
          row={open} kind={rowKind(open)} today={today} now={now} pic={picOf(open, rowKind(open))}
          description={describe(open).text} descriptionUrl={describe(open).url}
          showings={showingsOf(open)}
          saved={isSaved(open)} hidden={isHidden(open)}
          onSave={doSave} onHide={doHide} onClose={() => setSelected(null)}
        />
      )}
    </div>
    </div>
  )
}
